import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { OracleHero } from "@/components/runboom/oracle-hero";
import { Wordmark } from "@/components/runboom/marks";
import { heroPrediction, heroRunner, upcomingRaces, type Prediction } from "@/lib/mock";
import { getLines } from "@/lib/api";

export const Route = createFileRoute("/oracle/$raceSlug")({
  loader: ({ params }) => {
    const race = upcomingRaces.find((r) => r.slug === params.raceSlug);
    if (!race) throw notFound();
    const prediction =
      params.raceSlug === heroPrediction.raceSlug
        ? heroPrediction
        : {
            raceSlug: race.slug,
            raceName: race.name,
            distanceLabel: race.distanceLabel,
            p10Seconds: Math.max(0, race.yourPredictedSeconds - 85),
            p50Seconds: race.yourPredictedSeconds,
            p90Seconds: race.yourPredictedSeconds + 105,
            boomProbabilityPct: 58,
            drivers: [],
          };
    const pb = heroRunner.pbs.find((p) => p.distance === race.distanceLabel);
    const suggested = upcomingRaces
      .filter((r) => r.slug !== race.slug)
      .slice(0, 3);
    return { race, prediction, pbSeconds: pb?.timeSeconds ?? null, suggested };
  },
  component: OracleRacePage,
});

function OracleRacePage() {
  const { race, prediction: seedPrediction, pbSeconds, suggested } = Route.useLoaderData();
  const [prediction, setPrediction] = useState<Prediction>(seedPrediction);
  const [source, setSource] = useState<"strava" | "seed">("seed");

  // If the runner has a session and a live line for this race, swap the seed
  // prediction for the real one. Silent fallback to seed if not authed or no
  // matching line — the page never blocks on the network.
  useEffect(() => {
    let cancelled = false;
    getLines()
      .then((lines) => {
        if (cancelled) return;
        const match = lines.find((l) => l.raceSlug === race.slug);
        if (match && match.source === "strava") {
          setPrediction({
            raceSlug: match.raceSlug,
            raceName: match.raceName,
            distanceLabel: match.distanceLabel,
            p10Seconds: match.p10Seconds,
            p50Seconds: match.p50Seconds,
            p90Seconds: match.p90Seconds,
            boomProbabilityPct: match.boomProbabilityPct,
            drivers: match.drivers,
          });
          setSource("strava");
        }
      })
      .catch(() => {
        /* unauthed or worker unreachable — keep seed */
      });
    return () => {
      cancelled = true;
    };
  }, [race.slug]);

  return (
    <main className="mx-auto max-w-[1280px] px-5 py-6 sm:px-8 sm:py-10">
      <TopBar />

      <nav className="mt-4 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.1em] text-fg-muted">
        <Link to="/" className="hover:text-fg">RUNBOOM</Link>
        <span aria-hidden>/</span>
        <Link to="/oracle" className="hover:text-fg">Oracle</Link>
        <span aria-hidden>/</span>
        <span className="text-fg">{race.name}</span>
      </nav>

      <section className="mt-6 sm:mt-8">
        <OracleHero
          prediction={prediction}
          race={race}
          pbSeconds={pbSeconds}
          suggested={suggested}
          source={source}
        />
      </section>

      <footer className="mt-16 flex items-center justify-between border-t border-border pt-6 font-mono text-[11px] uppercase tracking-[0.1em] text-fg-subtle sm:mt-20">
        <span>Every PB is a BOOM.</span>
        <span>Run to be remembered.</span>
      </footer>
    </main>
  );
}

function TopBar() {
  return (
    <div className="flex items-center justify-between">
      <Link to="/" className="flex items-center gap-3">
        <Wordmark size="sm" />
      </Link>
      <nav className="flex items-center gap-3 font-mono text-[12px] uppercase tracking-[0.1em] text-fg-muted sm:gap-5">
        <Link to="/oracle" className="hidden hover:text-fg sm:inline">Oracle</Link>
        <Link to="/races" className="hidden hover:text-fg sm:inline">Races</Link>
        <Link
          to="/leaderboards"
          search={{ distance: "10K", city: "Cape Town" }}
          className="hidden hover:text-fg sm:inline"
        >
          Leaderboards
        </Link>
        <Link to="/wall" className="hidden hover:text-fg sm:inline">Wall</Link>
        <Link
          to="/me"
          className="rounded-full px-3 py-1.5 text-night transition-transform hover:scale-[1.02]"
          style={{ backgroundColor: "var(--color-acid-lime)" }}
        >
          Connect Strava
        </Link>
      </nav>
    </div>
  );
}
